import { coreDaoFactory } from '../common'
import { HttpResponse } from '../common/util/http-util'
import { errorUtil } from '../util/error-util'
import type {
  AdminGroupMembersListResponse,
  AdminGroupPostsListResponse,
  AdminGroupsListResponse,
  CreateGroupPayload,
  CreateGroupPostInput,
  DiscoverGroupsParams,
  Group,
  GroupMember,
  GroupMemberResponse,
  GroupMemberRole,
  GroupPost,
  GroupPostsListResponse,
  UpdateGroupPayload,
} from '../types/groups'

const dao = coreDaoFactory({
  onError: async (error: Error) => {
    errorUtil.handleHttpError(error)
  },
})

const unwrap = <T>(response: HttpResponse<T>): T[] => response.data

export const groupsDao = {
  discoverGroups: async (
    params?: DiscoverGroupsParams,
  ): Promise<{ data: (Group & { isJoined: boolean })[]; meta: { limit: number; offset: number; total: number } }> => {
    const result = await dao._http.get<any>('/groups/discover', { params })
    return {
      data: result.data.data as (Group & { isJoined: boolean })[],
      meta: result.data.meta ?? { limit: params?.limit ?? 20, offset: params?.offset ?? 0, total: 0 },
    }
  },

  myGroups: async (): Promise<Group[]> => {
    const result = await dao._http.get<HttpResponse<Group>>('/groups/me')
    return unwrap(result.data)
  },

  getGroupById: async (groupId: string): Promise<Group> => {
    const result = await dao._http.get<HttpResponse<Group>>(`/groups/${groupId}`)
    return unwrap(result.data)[0]
  },

  joinByInviteCode: async (inviteCode: string): Promise<GroupMemberResponse> => {
    const result = await dao._http.post<HttpResponse<GroupMemberResponse>>('/groups/join', { inviteCode })
    return unwrap(result.data)[0]
  },

  joinPublicGroup: async (groupId: string): Promise<GroupMemberResponse> => {
    const result = await dao._http.post<HttpResponse<GroupMemberResponse>>(`/groups/${groupId}/join`)
    return unwrap(result.data)[0]
  },

  leaveGroup: async (groupId: string): Promise<void> => {
    await dao._http.post(`/groups/${groupId}/leave`)
  },

  listMembers: async (groupId: string, params?: { limit?: number; offset?: number }): Promise<GroupMember[]> => {
    const result = await dao._http.get<HttpResponse<GroupMember>>(`/groups/${groupId}/members`, { params })
    return unwrap(result.data)
  },

  inviteMembers: async (groupId: string, hustlerIds: string[]): Promise<void> => {
    await dao._http.post(`/groups/${groupId}/members`, { hustlerIds })
  },

  changeMemberRole: async (groupId: string, memberId: string, role: GroupMemberRole): Promise<void> => {
    await dao._http.post(`/groups/${groupId}/members/${memberId}/role`, { role })
  },

  removeMember: async (groupId: string, memberId: string): Promise<void> => {
    await dao._http.delete(`/groups/${groupId}/members/${memberId}`)
  },

  updateGroup: async (groupId: string, data: UpdateGroupPayload): Promise<Group> => {
    const result = await dao._http.patch<HttpResponse<Group>>(`/groups/${groupId}`, data)
    return unwrap(result.data)[0]
  },

  deleteGroup: async (groupId: string): Promise<void> => {
    await dao._http.delete(`/groups/${groupId}`)
  },

  regenerateInviteCode: async (groupId: string): Promise<Group> => {
    const result = await dao._http.post<HttpResponse<Group>>(`/groups/${groupId}/invite-code/regenerate`)
    return unwrap(result.data)[0]
  },

  listGroupPosts: async (groupId: string, params?: { limit?: number; offset?: number }): Promise<GroupPostsListResponse> => {
    const result = await dao._http.get<any>(`/groups/${groupId}/posts`, { params })
    return {
      data: result.data.data as GroupPost[],
      limit: params?.limit ?? 20,
      offset: params?.offset ?? 0,
      total: result.data.meta?.total ?? result.data.data?.length ?? 0,
    }
  },

  createGroupPost: async (groupId: string, data: CreateGroupPostInput): Promise<GroupPost> => {
    const result = await dao._http.post<HttpResponse<GroupPost>>(`/groups/${groupId}/posts`, data)
    return unwrap(result.data)[0]
  },

  deleteGroupPost: async (groupId: string, postId: string): Promise<void> => {
    await dao._http.delete(`/groups/${groupId}/posts/${postId}`)
  },

  togglePinPost: async (groupId: string, postId: string): Promise<void> => {
    await dao._http.patch(`/groups/${groupId}/posts/${postId}/pin`)
  },

  // Admin methods
  adminListGroups: async (params: { limit: number; offset: number; search?: string }): Promise<AdminGroupsListResponse> => {
    const result = await dao._http.get<any>('/admin/groups', { params })
    return {
      data: result.data.data as Group[],
      total: result.data.meta?.total ?? result.data.data?.length ?? 0,
    }
  },

  adminCreateGroup: async (data: CreateGroupPayload): Promise<Group> => {
    const result = await dao._http.post<HttpResponse<Group>>('/admin/groups', data)
    return unwrap(result.data)[0]
  },

  adminGetGroup: async (groupId: string): Promise<Group> => {
    const result = await dao._http.get<HttpResponse<Group>>(`/admin/groups/${groupId}`)
    return unwrap(result.data)[0]
  },

  adminUpdateGroup: async (groupId: string, data: UpdateGroupPayload): Promise<Group> => {
    const result = await dao._http.patch<HttpResponse<Group>>(`/admin/groups/${groupId}`, data)
    return unwrap(result.data)[0]
  },

  adminDeleteGroup: async (groupId: string): Promise<void> => {
    await dao._http.delete(`/admin/groups/${groupId}`)
  },

  adminListMembers: async (
    groupId: string,
    params: { limit: number; offset: number },
  ): Promise<AdminGroupMembersListResponse> => {
    const result = await dao._http.get<any>(`/admin/groups/${groupId}/members`, { params })
    return {
      data: result.data.data as GroupMemberResponse[],
      total: result.data.meta?.total ?? result.data.data?.length ?? 0,
    }
  },

  adminAddMembers: async (groupId: string, hustlerIds: string[]): Promise<void> => {
    await dao._http.post(`/admin/groups/${groupId}/members`, { hustlerIds })
  },

  adminRemoveMember: async (groupId: string, memberId: string): Promise<void> => {
    await dao._http.delete(`/admin/groups/${groupId}/members/${memberId}`)
  },

  adminListPosts: async (groupId: string, params: { limit: number; offset: number }): Promise<AdminGroupPostsListResponse> => {
    const result = await dao._http.get<any>(`/admin/groups/${groupId}/posts`, { params })
    return {
      data: result.data.data as GroupPost[],
      total: result.data.meta?.total ?? result.data.data?.length ?? 0,
    }
  },
}
